import { Map as ImmutableMap, Set as ImmutableSet, List, Record, isImmutable } from 'immutable';
import { DateTime } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { securitiesUtils } from 'app-components/flux/securities';
import { accountsSelectors } from '../accounts';
import { selectorsHelpers } from '../core';
import { investmentQuotesV2Selectors, investmentQuotesV2Utils } from '../investment-quotes-v2';

import { INVESTMENT_HOLDINGS_V2_REDUCER_KEY, columns } from './investmentHoldingsV2Types';

export const getResourceStore = (state) => state[INVESTMENT_HOLDINGS_V2_REDUCER_KEY];

export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const getIsLoading = (state) => getResourceStore(state).isLoading;
export const getError = (state) => getResourceStore(state).error;
export const getResourcesById = (state) => getResourceStore(state).resourcesById;

export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;

export const getManualHoldingsWithLocalSecurityId = createSelector(
  getResourcesById,
  accountsSelectors.getNonConnectedInvestmentAccounts,
  (state, localSecurityId) => localSecurityId,
  (holdings, accounts, localSecurityId) => {
    const holdingsInAccounts = holdings.filter((holding) => accounts.has(holding.accountId));
    return ImmutableMap(holdingsInAccounts.filter((holding) => holding.localSecurityId === localSecurityId));
  },
);

const getInvestmentHoldingsForAccount = createSelector(
  getResourcesById,
  (state, accountId) => accountId,
  (holdings, accountId) => {
    if (!accountId) {
      return holdings;
    }
    return holdings.filter((holding) => holding.accountId === accountId);
  },
);

export const getInvestmentHoldingsByAccountId = createSelector(getResourcesById, (holdings) =>
  holdings.toList().groupBy((holding) => holding.accountId),
);

export const getInvestmentHoldingsIdsByAccountId = selectorsHelpers.createTrimSelector(
  getInvestmentHoldingsByAccountId,
  (holdingList) => holdingList.map(({ id }) => id),
);

export const getCashByAccountId = createSelector(
  getInvestmentHoldingsByAccountId,
  (state, props) => props.accountId,
  (holdings, accountId) => (accountId ? holdings.get(accountId)?.find((holding) => holding.isCash) : null),
);

/*
 *  This is done because the marketValue and price in manual holdings is NOT updated every refresh to reflect a
 *  non-manual stocks' new price. So we take the quantity and multiply it by the new price, pass it to this function,
 *  and now we have updated values!
 */
export const rebuildHoldingWithRealValues = (name, marketValue, price, holdingItem, accountType) => {
  if (!holdingItem) {
    return undefined;
  }
  return {
    name, //  accountName, the only NEW value that is not usually present
    accountId: holdingItem.accountId,
    subType: accountType,
    clientId: holdingItem.clientId,
    createdAt: holdingItem.createdAt,
    currentPrice: price,
    id: holdingItem.id,
    isCash: holdingItem.isCash,
    isDeleted: holdingItem.isDeleted,
    marketValue,
    quantity: holdingItem.quantity,
    securityName: holdingItem.securityName,
    symbol: holdingItem?.symbol?.trim(),
    localSecurityId: holdingItem?.localSecurityId,
    costBasis: holdingItem?.costBasis,
    userCostBasis: holdingItem?.userCostBasis,
    userCostBasisOn: holdingItem?.userCostBasisOn,
    quantityWhenUserSetCostBasis: holdingItem?.quantityWhenUserSetCostBasis,
  };
};

/*
 *  Get the sorted holdings of a specific stock
 *  stockData: All of the information on the selected stock
 */
export const getSortedAccountsMap = createSelector(
  accountsSelectors.getInvestmentAccounts,
  (state, props) => props.node,
  (state) => investmentQuotesV2Selectors.getQuoteListByLocalSecurityId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (investmentAccounts, stockData, quotesBySecurityId, securitiesFF) => {
    if (investmentAccounts && stockData) {
      const filteredAccounts = investmentAccounts.filter((account) => {
        let isContained = false;
        stockData.holdings.forEach((myHolding) => {
          if (myHolding.accountId === account.id) {
            isContained = true;
          }
        });
        return isContained;
      });
      const newList = filteredAccounts.map((acct) => {
        const { name } = acct;
        let price = 0;
        let marketValue = 0;
        let holdingItem;
        stockData.holdings.forEach((myHolding) => {
          if (myHolding.accountId === acct.id) {
            if (securitiesFF) {
              const quote = quotesBySecurityId.get(myHolding?.localSecurityId);
              const isBarchartQuote = 'lastPrice' in (quote || {});
              price = securitiesUtils.getPrice(quote);
              marketValue =
                myHolding.isCash || !isBarchartQuote
                  ? myHolding.marketValue || (typeof price === 'number' ? price : stockData.price) * myHolding.quantity
                  : (typeof price === 'number' ? price : stockData.price) * myHolding.quantity;
            } else {
              const quote = investmentQuotesV2Utils.getQuoteBySymbol(quotesBySecurityId, myHolding?.symbol?.trim());
              price = quote?.currentPrice || stockData?.price || myHolding?.currentPrice || 0;
              marketValue =
                typeof price === 'number' && !myHolding.isCash && quote
                  ? price * myHolding.quantity
                  : myHolding.marketValue;
            }
            holdingItem = myHolding;
          }
        });
        return rebuildHoldingWithRealValues(name, marketValue, price, holdingItem, acct.subType);
      });
      return newList.sort((a, b) => a.marketValue - b.marketValue);
    }
    return null;
  },
);

const calcValue = ({ quantity, marketValue, isCash, currentPrice }, lastQuote, securitiesFF) => {
  let lastPrice;
  if (securitiesFF) {
    lastPrice = securitiesUtils.getPrice(lastQuote);
  } else {
    lastPrice = lastQuote?.lastPrice || currentPrice || 0;
  }

  const isBarchartQuote = 'lastPrice' in (lastQuote || {});
  if (typeof lastPrice === 'number' && typeof quantity === 'number' && !isCash && isBarchartQuote) {
    return lastPrice * quantity;
  } else if (typeof marketValue === 'number' && (isCash || !isBarchartQuote)) {
    // Cash holdings will not have a quote or security
    return marketValue;
  }
  return 0;
};

const holdingValue = (holding, quotes, securitiesFF) => {
  let quote;
  if (securitiesFF) {
    quote = quotes.get(holding.localSecurityId);
  } else {
    quote = investmentQuotesV2Utils.getQuoteBySymbol(quotes, holding?.symbol?.trim());
  }
  return calcValue(holding, quote, securitiesFF);
};

const HoldingNode = Record({
  name: undefined,
  key: undefined,
  symbol: undefined,
  shares: undefined,
  price: undefined,
  value: undefined,
  todayPercent: undefined,
  todayValue: undefined,
  sumOfCostBasis: undefined,
  quote: undefined,
  isCash: undefined,
  holdings: [],
  localSecurityId: undefined,
  costBasis: undefined,
  userCostBasis: undefined,
  userCostBasisOn: undefined,
  quantityWhenUserSetCostBasis: undefined,
  netGain: undefined,
  costBasisStatus: undefined,
});

export const getHoldingNode = createSelector(
  (state, props) => getInvestmentHoldingNodes(state, props?.accounts),
  (state, props) => props.node,
  (allNodes, selectedNode) => selectedNode && allNodes.find((node) => node.key === selectedNode.key),
);

export const getSymbols = createSelector(
  (state, holdings) => holdings || (state && getResourcesById(state)),
  (investmentHoldings) =>
    ImmutableSet().withMutations(
      (symbols) =>
        investmentHoldings &&
        investmentHoldings.reduce(
          (set, investmentHolding) => (investmentHolding?.symbol ? set.add(investmentHolding.symbol?.trim()) : set),
          symbols,
        ),
    ),
);

export const getHoldingsValues = createSelector(
  selectorsHelpers.createTrimSelector(getResourcesById, (holding) => ({
    quantity: holding.quantity,
    marketValue: holding.marketValue,
    isCash: holding.isCash,
    currentPrice: holding?.currentPrice,
  })),
  (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (trimmedHoldings, lastQuotes, securitiesFF) =>
    trimmedHoldings.map((trimmedHolding, id) => calcValue(trimmedHolding, lastQuotes.get(id)), securitiesFF),
);

export const getHoldingChanges = createSelector(
  selectorsHelpers.createTrimSelector(getResourcesById, (holding) => holding.quantity ?? 0),
  selectorsHelpers.createTrimSelector(
    (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
    (quote) => quote,
  ),
  (holdingQuantities, amtChanges) =>
    holdingQuantities.map((quantity, id) => quantity * (amtChanges.get(id)?.amtChange ?? 0)),
);

export const getHoldingProfit = createSelector(
  selectorsHelpers.createTrimSelector(getResourcesById, (holding) => ({
    value: holding.marketValue,
    costBasis: holding.costBasis ?? holding.userCostBasis,
    quantity: holding.quantity,
    isCash: holding.isCash,
    isUserCostBasisSet: holding.userCostBasis !== undefined,
    quantityWhenUserSetCostBasis: holding.quantityWhenUserSetCostBasis,
  })),
  (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (trimmedHoldings, lastQuotes, securitiesFF) =>
    trimmedHoldings.map((trimmedHolding, id) => {
      const lastQuote = lastQuotes.get(id);
      const quantityChangeNeeded =
        trimmedHolding.isUserCostBasisSet &&
        trimmedHolding.quantityWhenUserSetCostBasis &&
        trimmedHolding.quantity !== trimmedHolding.quantityWhenUserSetCostBasis;
      const lastPrice = securitiesFF
        ? securitiesUtils.getPrice(lastQuote)
        : lastQuote?.lastPrice * trimmedHolding.quantity || trimmedHolding.value || 0;
      if (!lastPrice || trimmedHolding.costBasis === undefined || quantityChangeNeeded) {
        return { profit: 0, costBasis: undefined, isCash: trimmedHolding.isCash };
      }
      return { profit: lastPrice - trimmedHolding.costBasis, costBasis: trimmedHolding.costBasis };
    }),
);
export const getHoldingsTotals = createCachedSelector(
  (state) => getHoldingsValues(state),
  (state) => getHoldingChanges(state),
  (state, accounts) => accounts,
  (state) => getInvestmentHoldingsIdsByAccountId(state),
  (state) => getHoldingProfit(state),
  (holdingValues, holdingChanges, accountIds, holdingIdsByAccountId, totalCostBasis) =>
    accountIds
      ?.map((id) => holdingIdsByAccountId.get(id))
      .flatten()
      .reduce(
        (accumulator, holdingId) => {
          const todaysBalance = holdingValues.get(holdingId);
          const todaysBalanceChange = holdingChanges.get(holdingId);
          const totalCostBasisInfo = totalCostBasis.get(holdingId);
          const profit = totalCostBasisInfo?.profit;
          const costBasis = totalCostBasisInfo?.costBasis;
          const isCash = totalCostBasisInfo?.isCash;
          accumulator.todaysBalance =
            typeof accumulator.todaysBalance === 'number'
              ? accumulator.todaysBalance + (todaysBalance || 0)
              : todaysBalance;

          accumulator.todaysBalanceChange =
            typeof accumulator.todaysBalanceChange === 'number'
              ? accumulator.todaysBalanceChange + (todaysBalanceChange || 0)
              : todaysBalanceChange;
          accumulator.totalProfit = isCash
            ? accumulator.totalProfit
            : typeof accumulator.totalProfit === 'number'
              ? accumulator.totalProfit + (profit || 0)
              : profit;
          accumulator.totalCostBasis =
            isCash || !totalCostBasisInfo
              ? accumulator.totalCostBasis
              : accumulator.totalCostBasis === undefined || costBasis === undefined
                ? undefined
                : typeof accumulator.totalCostBasis === 'number'
                  ? accumulator.totalCostBasis + (costBasis || 0)
                  : costBasis;
          return accumulator;
        },
        {
          todaysBalance: undefined,
          todaysBalanceChange: undefined,
          totalProfit: undefined,
          totalCostBasis: false,
        },
      ),
)({
  keySelector: (_state, accounts) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getHoldingsTotalsByAccountId = createSelector(
  (state) => getHoldingsValues(state),
  (state) => accountsSelectors.getAccountsById(state),
  (state) => getInvestmentHoldingsIdsByAccountId(state),
  (holdingValues, accounts, holdingIdsByAccountId) =>
    accounts.map((account) => {
      const holdings = holdingIdsByAccountId.get(account.id);
      let totalAccountValue = 0;
      holdings?.forEach((holding) => {
        totalAccountValue += holdingValues.get(holding);
      });
      return totalAccountValue;
    }),
);

const getHoldingNodesByHoldingId = createSelector(
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (state) => investmentQuotesV2Selectors.getQuoteListByHoldingId(state),
  (state) => getHoldingsValues(state),
  (state) => getHoldingChanges(state),
  getResourcesById,
  (securitiesFF, quotes, values, changes, holdings) => {
    return holdings.map((holding, id) => {
      const quote = quotes.get(id); // Can be the security OR connected quote
      const value = values.get(id);
      const todayValue = changes.get(id);

      let name;
      let key;
      let symbol;
      let price;

      const costBasis = holding?.costBasis;
      const userCostBasis = holding?.userCostBasis;
      const userCostBasisOn = holding?.userCostBasisOn;
      const quantityWhenUserSetCostBasis = holding?.quantityWhenUserSetCostBasis;
      const noCostBasis = !holding?.isCash && holding?.costBasis === undefined && holding?.userCostBasis === undefined;
      const quantityChangeNeeded =
        holding?.userCostBasis !== undefined &&
        quantityWhenUserSetCostBasis &&
        holding.quantity !== quantityWhenUserSetCostBasis;
      const costBasisStatus = {
        noCostBasis,
        ...((noCostBasis || quantityChangeNeeded) && { incompleteCostBasisHoldings: [holding] }),
      };
      if (securitiesFF) {
        name = holding.isCash ? undefined : quote?.name || quote?.symbol?.trim() || holding?.securityName || undefined;
        key = holding.isCash ? 'Simplifi-Cash' : quote?.symbol?.trim() || name || holding?.localSecurityId || 'Unknown';
        if (key === 'Unknown') {
          name = 'Unknown';
          symbol = null;
        } else {
          symbol = holding.isCash ? null : quote?.symbol?.trim() || holding?.symbol?.trim() || null;
        }
        price =
          (quote && !holding.isCash && securitiesUtils.getPrice(quote)) ||
          (holding.isCash && holding?.holdings?.[0]?.currentPrice) ||
          null;
      } else {
        name = holding.isCash
          ? undefined
          : (quote && quote.name) || holding.securityName || holding.symbol || undefined;
        key = holding.isCash ? 'Simplifi-Cash' : holding.symbol?.trim() || name;
        price =
          (quote && quote?.lastPrice) ||
          (!quote && holding?.currentPrice) ||
          (holding && !holding.symbol?.trim() && holding?.holdings?.[0]?.currentPrice) ||
          null;
        symbol = holding?.symbol?.trim();
      }
      const finalCostBasis = costBasis ?? userCostBasis;
      const netGain = finalCostBasis !== undefined ? Math.round((value - finalCostBasis) * 10000) / 10000 : undefined;
      return HoldingNode({
        name,
        key,
        symbol,
        shares: holding.isCash ? null : holding.quantity,
        price,
        value,
        todayPercent: quote?.pctChange,
        todayValue,
        sumOfCostBasis: finalCostBasis, // this is for summing up cost basis amongst nodes
        quote,
        isCash: holding.isCash,
        holdings: [holding],
        localSecurityId: holding?.localSecurityId,
        costBasis,
        userCostBasis,
        userCostBasisOn,
        quantityWhenUserSetCostBasis,
        netGain,
        costBasisStatus,
      });
    });
  },
);

const sumHoldingNodes = (oldOrEmpty, newNode) =>
  oldOrEmpty
    ? newNode
        .updateIn(['value'], (newValue) => (newValue ?? 0) + (oldOrEmpty.value ?? 0))
        .updateIn(['todayValue'], (newTodayValue) => (newTodayValue ?? 0) + (oldOrEmpty.todayValue ?? 0))
        .updateIn(['shares'], (newShares) => (newShares ?? 0) + (oldOrEmpty.shares ?? 0))
        .updateIn(['holdings'], (newHoldings) => [...(newHoldings ?? []), ...(oldOrEmpty.holdings ?? [])])
        .updateIn(['costBasis'], (newCostBasis) => {
          const oldCostBasis = oldOrEmpty.costBasis;
          if (newCostBasis === undefined) return oldCostBasis;
          if (oldCostBasis === undefined) return newCostBasis;
          return newCostBasis + oldCostBasis ?? undefined;
        })
        .updateIn(['sumOfCostBasis'], () => {
          const costBasis = newNode.sumOfCostBasis;
          const oldCostBasis = oldOrEmpty.sumOfCostBasis;
          if (costBasis === undefined) return oldCostBasis;
          if (oldCostBasis === undefined) return costBasis;
          return costBasis + oldCostBasis ?? undefined;
        })
        .updateIn(['userCostBasis'], (newUserCostBasis) => {
          const oldUserCostBasis = oldOrEmpty.userCostBasis;
          if (newUserCostBasis === undefined) return oldUserCostBasis;
          if (oldUserCostBasis === undefined) return newUserCostBasis;

          return newUserCostBasis + oldUserCostBasis ?? undefined;
        })
        .updateIn(['netGain'], (newNetGain) => {
          const oldNetGain = oldOrEmpty.netGain;
          if (newNetGain === undefined) return oldNetGain;
          if (oldNetGain === undefined) return newNetGain;
          return newNetGain + oldNetGain ?? undefined;
        })
        .updateIn(['costBasisStatus'], (newCostBasisStatus) => ({
          noCostBasis: !!newCostBasisStatus.noCostBasis && !!oldOrEmpty.costBasisStatus.noCostBasis,
          incompleteCostBasisHoldings: newCostBasisStatus.incompleteCostBasisHoldings
            ? [
                ...newCostBasisStatus.incompleteCostBasisHoldings,
                ...(oldOrEmpty.costBasisStatus.incompleteCostBasisHoldings ?? []),
              ]
            : oldOrEmpty.costBasisStatus.incompleteCostBasisHoldings,
        }))
    : newNode;

export const getInvestmentHoldingNodes = createCachedSelector(
  getHoldingNodesByHoldingId,
  (state, accounts) =>
    List.isList(accounts)
      ? accounts
      : accountsSelectors.accountIds(state, accounts || accountsSelectors.getInvestmentAccounts(state)),
  getInvestmentHoldingsIdsByAccountId,
  (holdingNodes, accountIds, holdingsByAccountId) => {
    const relevantHoldingNodes = accountIds
      .map((id) => holdingsByAccountId.get(id))
      .toList()
      .flatten()
      .map((holdingId) => holdingNodes.get(holdingId));

    return ImmutableMap()
      .withMutations((draft) =>
        relevantHoldingNodes.reduce((acc, node) => {
          if (!node) {
            return acc;
          }
          return acc.set(node.key, sumHoldingNodes(acc.get(node.key), node));
        }, draft),
      )
      .toList();
  },
)({
  keySelector: (_state, accounts) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getInvestmentHoldingsBalancesByAccountId = createSelector(
  getResourcesById,
  (state) => investmentQuotesV2Selectors.getQuoteListByLocalSecurityId(state),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'relyOnSecurities'),
  (function setupResultsCache() {
    let resultsCache;
    return (holdings, quotes, securitiesFF) => {
      const resultsFresh = ImmutableMap().withMutations((investmentHoldingsBalancesByAccountId) => {
        holdings.forEach((holding) =>
          investmentHoldingsBalancesByAccountId.set(
            holding.accountId,
            (investmentHoldingsBalancesByAccountId.get(holding.accountId) || 0) +
              holdingValue(holding, quotes, securitiesFF),
          ),
        );
      });
      resultsCache = resultsFresh.equals(resultsCache) ? resultsCache : resultsFresh;
      return resultsCache;
    };
  })(),
);

export const getStockString = createSelector(getInvestmentHoldingNodes, (nodes) =>
  nodes
    ?.filter((node) => node?.symbol && node?.quote)
    .map((node) => node?.symbol?.trim())
    .join(','),
);

export const getAllStockSymbols = createSelector(getInvestmentHoldingNodes, (nodes) =>
  nodes?.filter((node) => node?.symbol && node?.quote).map((node) => node?.symbol?.trim()),
);

export const getSortedHoldingNodes = createCachedSelector(
  (state, props) => getInvestmentHoldingNodes(state, props.accounts),
  (state, props) => props.sortDirection,
  (state, props) => props.orderBy,
  (nodes, sortDirection, orderBy) =>
    nodes?.sortBy(
      (myStock) => myStock[orderBy],
      (unalteredValueA, unalteredValueB) => {
        if (orderBy === columns[0].key || orderBy === columns[1].key) {
          // These keys use strings!
          const last = String.fromCharCode(0xffff);
          const valueA = unalteredValueA ? String(unalteredValueA) : last; //  Undefined values go to the end of the list
          const valueB = unalteredValueB ? String(unalteredValueB) : last;
          if (sortDirection === 'asc') {
            return valueA.localeCompare(valueB, undefined);
          }
          return valueB.localeCompare(valueA, undefined);
        }

        const last = Number.NEGATIVE_INFINITY;
        const valueA = unalteredValueA != null ? Number(unalteredValueA) : last; //  Undefined values go to the end of the list
        const valueB = unalteredValueB != null ? Number(unalteredValueB) : last;

        //  Using regular numeric comparisons because localeCompare numeric does NOT account for negative values!
        return (valueA - valueB) * (sortDirection === 'asc' ? -1 : 1);
      },
    ),
)(
  (state, props) =>
    `${props?.context ?? ''}_${
      isImmutable(props?.accounts) ? props?.accounts?.hashCode() : props?.accounts?.toString()
    }_${props?.sortDirection}_${props?.orderBy}`,
  { cacheObject: new LruObjectCache({ cacheSize: 10 }) },
);

export const getMostRecentStock = createSelector(
  (state, accounts) => getInvestmentHoldingNodes(state, accounts),
  (nodes) => {
    let recentNode = null;
    nodes.forEach((node) => {
      if (node?.quote?.quoteTime) {
        recentNode = recentNode?.quote?.quoteTime > node?.quote?.quoteTime ? recentNode : node;
      }
    });
    return recentNode;
  },
);

export const getTimeStrings = createCachedSelector(
  getMostRecentStock,
  getHoldingsTotals,
  (mostRecentStock, holdingTotals) => {
    const originalTime = mostRecentStock?.quote?.quoteTime;
    if (!originalTime) {
      return '\xA0'; //  This is done to preserve spacing
    }
    const originalDateTime = DateTime.fromISO(originalTime);
    const timeString = originalDateTime.toFormat('MMM dd, yyyy • h:mm a ZZZZ');

    const { todaysBalance, todaysBalanceChange } = holdingTotals;

    const todaysPercentageChange =
      typeof todaysBalance === 'number' &&
      typeof todaysBalanceChange === 'number' &&
      todaysBalance !== todaysBalanceChange
        ? (100 * todaysBalanceChange) / (todaysBalance - todaysBalanceChange)
        : undefined;
    const todaysPercentageChangeString = todaysPercentageChange
      ? `(${todaysPercentageChange > 0 ? '+' : ''}${todaysPercentageChange.toFixed(2)}%)`
      : '(-)';

    return { timeString, todaysPercentageChange, todaysPercentageChangeString };
  },
)({
  keySelector: (_state, accounts) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
});

export const getHoldingByAccountIdAndSymbolOrName = createSelector(
  (state, props) => {
    if (!props.accountId) {
      return undefined;
    }
    return getInvestmentHoldingsForAccount(state, props.accountId);
  },
  (state, props) => props.accountId,
  (state, props) => props.symbol || '',
  (state, props) => props.holdingName || '',
  (holdings, accountId, symbol, holdingName) =>
    (accountId &&
      holdingName &&
      holdings &&
      holdings.find(
        (holding) =>
          holding?.accountId === accountId &&
          ((symbol && holding?.symbol?.trim() === symbol) ||
            (holdingName === 'Simplifi-Cash' && holding.isCash) ||
            (holdingName && holding?.name === holdingName) ||
            (holdingName && holding?.securityName === holdingName)),
      )) ||
    undefined,
);

export const getHoldingNodeByKey = createSelector(
  (state, props) => getInvestmentHoldingNodes(state, props.accounts),
  (state, props) => props.holdingNodeKey,
  (nodes, holdingNodeKey) => nodes?.find((node) => node?.key === holdingNodeKey),
);
